import React from "react";
import { Box, Skeleton } from "@mui/material";
import ReactECharts from "echarts-for-react";
import useChartTheme from "../useChartTheme";
import type { VisualizationMeta } from "hooks/useNLQuery";

interface Props {
  rows: Record<string, unknown>[];
  columns: string[];
  visualization: VisualizationMeta;
  loading?: boolean;
}

const AgenticBarChart: React.FC<Props> = ({ rows, columns, visualization, loading }) => {
  const { baseAxis, baseTooltip, baseGrid, seriesColors, textPrimary, chartHeight } =
    useChartTheme();

  if (loading || !rows.length) {
    return <Skeleton variant="rounded" height={chartHeight} sx={{ borderRadius: 2 }} />;
  }

  const xCol = visualization.x_axis || columns.find((c) => c !== "patient_id") || columns[0];
  const yCol = visualization.y_axis || columns.find((c) => c !== xCol && c !== "patient_id") || columns[1];
  const seriesCol = visualization.series;

  if (!xCol || !yCol) return null;

  if (seriesCol && visualization.type === "grouped_bar") {
    const groups = [...new Set(rows.map((r) => String(r[seriesCol] ?? "")))];
    const categories = [...new Set(rows.map((r) => String(r[xCol] ?? "")))];

    const series = groups.map((group, i) => ({
      name: group,
      type: "bar" as const,
      data: categories.map((cat) => {
        const match = rows.find((r) => String(r[xCol]) === cat && String(r[seriesCol]) === group);
        return match ? Number(match[yCol] ?? 0) : 0;
      }),
      itemStyle: { borderRadius: [4, 4, 0, 0] },
      barMaxWidth: 28,
      color: seriesColors[i % seriesColors.length],
    }));

    const option = {
      tooltip: { ...baseTooltip, trigger: "axis" as const, axisPointer: { type: "shadow" as const } },
      legend: { textStyle: { color: textPrimary, fontSize: 11 }, bottom: 0 },
      grid: { ...baseGrid, bottom: "15%" },
      xAxis: { ...baseAxis, type: "category" as const, data: categories },
      yAxis: { ...baseAxis, type: "value" as const },
      series,
    };

    return (
      <Box sx={{ width: "100%", height: chartHeight }}>
        <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
      </Box>
    );
  }

  const labels = rows.map((r) => String(r[xCol] ?? ""));
  const values = rows.map((r) => Number(r[yCol] ?? 0));

  const option = {
    tooltip: { ...baseTooltip, trigger: "axis" as const, axisPointer: { type: "shadow" as const } },
    grid: baseGrid,
    xAxis: {
      ...baseAxis,
      type: "category" as const,
      data: labels,
      axisLabel: { ...baseAxis.axisLabel, rotate: labels.length > 8 ? 30 : 0 },
    },
    yAxis: { ...baseAxis, type: "value" as const },
    series: [
      {
        type: "bar" as const,
        data: values,
        itemStyle: { color: seriesColors[0], borderRadius: [4, 4, 0, 0] },
        barMaxWidth: 36,
      },
    ],
  };

  return (
    <Box sx={{ width: "100%", height: chartHeight }}>
      <ReactECharts option={option} style={{ width: "100%", height: "100%" }} opts={{ renderer: "canvas" }} />
    </Box>
  );
};

export default AgenticBarChart;
